﻿import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { TABLE_HEADERS } from "@/utils/constants/appConstants/commonConstants";
import { DateFormatter, TimeFormatter } from "@/utils/functions/helperFunctions";
import { TbUserEdit } from "react-icons/tb";
import { useCompactListLayout } from "@/utils/hooks/useCompactListLayout";
import { NO_ROLE_YET, CREATE_YOUR_ROLES } from "@/utils/constants/messages";
import NoDataAvailable from "@/components/cards/NoDataAvailable";
import { MobileDateField, MobileLabelValue } from "@/components/cards/MobileDateField";
import { MobileListChevron } from "@/components/cards/MobileListChevron";
import {
    RoleTableSkeletonDesktop,
    RoleTableSkeletonMobile,
} from "@/components/skeletons";

interface RoleTableProps {
    roles: [];
    setRoles: (roles: any) => void;
    isLoading: boolean;
    dataLength: number;
    sorting: string;
    allFetched?: boolean;
    setSorting: ({}) => void;
    setPageNo: (pageNo: number) => void;
    dataLoad: boolean;
    id: string;
}

const RoleTable: React.FC<RoleTableProps> = (props) => {
    const [nameStatus, setNameStatus] = React.useState(true);
    const [createdAtStatus, setCreatedAtStatus] = React.useState(true);
    const [sortElement, setSortElement] = React.useState("created_at");
    const navigate = useNavigate();
    const isCompactLayout = useCompactListLayout();

    const nameSort = () => {
        setSortElement("name");
        props.setPageNo(1);
        props.setSorting({ name: !nameStatus ? 1 : -1 });
        setNameStatus(!nameStatus);
    };

    const createdAtSort = () => {
        setCreatedAtStatus((prevStatus) => !prevStatus);
        setSortElement("created_at");
        props.setPageNo(1);
        props.setSorting({ created_at: createdAtStatus ? 1 : -1 });
    };

    const roles = Array.isArray(props.roles) ? props.roles : [];
    const isPendingInitialLoad = props.dataLoad || (!roles.length && props.isLoading);
    const isLoadingMore = props.isLoading && !props.dataLoad;

    if (isCompactLayout) {
        if (isPendingInitialLoad) {
            return (
                <div className="gs-users-table-wrap">
                    <RoleTableSkeletonMobile />
                </div>
            );
        }

        if (!roles.length) {
            return (
                <NoDataAvailable>
                    <h4 className="text-center" id={`${props.id}-no-data-mb`}>
                        {NO_ROLE_YET}
                    </h4>
                    <p className="text-center" id={`${props.id}-no-data-message-mb`}>
                        {CREATE_YOUR_ROLES}
                    </p>
                </NoDataAvailable>
            );
        }

        return (
            <div className="gs-users-table-wrap">
                {roles.map((role: any, key: number) => (
                    <div className="gs-users-mobile-card gs-mobile-list-card" key={role.id ?? key}>
                        <div className="gs-mobile-card-icon">
                            <TbUserEdit id={`${props.id}-icon-${key}-mb`} />
                        </div>
                        <div className="gs-users-mobile-meta">
                            <p className="gs-users-table-user-email">{role?.name}</p>
                            <MobileLabelValue
                                label="Permissions: "
                                value={role?.features?.length}
                                id={`${props.id}-perimission-${key}-mb`}
                            />
                            <MobileLabelValue
                                label="Users: "
                                value={role?.user_count}
                                id={`${props.id}-user-count-${key}-mb`}
                            />
                            <MobileDateField
                                label="Created At: "
                                value={role.created_at}
                                dateId={`${props.id}-created-at-date-${key}-mb`}
                                timeId={`${props.id}-created-at-time-${key}-mb`}
                            />
                        </div>
                        <MobileListChevron
                            id={`${props.id}-redirect-${key}-mb`}
                            onClick={() => navigate(`/role/${role.id}`)}
                        />
                    </div>
                ))}
                {isLoadingMore ? <RoleTableSkeletonMobile rowCount={2} /> : null}
            </div>
        );
    }

    return (
        <div className="gs-users-table-wrap">
            <table
                className="nk-tb-list nk-tb-ulist table-weight"
                style={{ tableLayout: "fixed", width: "100%" }}
            >
                <colgroup>
                    <col className="gs-nk-w-40" />
                    <col className="gs-nk-w-20" />
                    <col className="gs-nk-w-20" />
                    <col className="gs-nk-w-20" />
                </colgroup>
                <thead>
                    <tr className="nk-tb-item nk-tb-head">
                        <th className="nk-tb-col gs-nk-w-40 cursor-pointer" onClick={nameSort}>
                            <span id={`${props.id}-name-th`}>{TABLE_HEADERS.NAME}</span>
                            <em
                                id={`${props.id}-name-th-sort-icon`}
                                className={`ml-1 icon ni ${sortElement === "name" ? (nameStatus ? "ni-upword-alt-fill" : "ni-downward-alt-fill") : ""}`}
                            />
                        </th>
                        <th className="nk-tb-col gs-nk-w-20">
                            <span id={`${props.id}-permission-th`}>{TABLE_HEADERS.PERMISSION}</span>
                        </th>
                        <th className="nk-tb-col gs-nk-w-20">
                            <span id={`${props.id}-user-th`}>{TABLE_HEADERS.USER}</span>
                        </th>
                        <th className="nk-tb-col gs-nk-w-20 cursor-pointer" onClick={createdAtSort}>
                            <span id={`${props.id}-created-at-th`}>{TABLE_HEADERS.CREATED_AT}</span>
                            <em
                                id={`${props.id}-created-at-th-sort-icon`}
                                className={`ml-1 icon ni ${sortElement === "created_at" ? (createdAtStatus ? "ni-upword-alt-fill" : "ni-downward-alt-fill") : ""}`}
                            />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {!isPendingInitialLoad ? (
                        roles.length > 0 ? (
                            <>
                                {roles.map((role: any, key: number) => (
                                    <tr className="nk-tb-item" key={role.id}>
                                        <td className="nk-tb-col">
                                            <Link to={`/role/${role?.id}`} className="user-card">
                                                <div className="icon fs-5">
                                                    <TbUserEdit className="icons mr-3" id={`${props.id}-icon-${key}`} />
                                                </div>
                                                <div className="user-info">
                                                    <span className="tb-lead" id={`${props.id}-name-${key}`}>
                                                        {role.name}
                                                    </span>
                                                </div>
                                            </Link>
                                        </td>
                                        <td className="nk-tb-col">
                                            <span className="tb-amount" id={`${props.id}-permission-${key}`}>
                                                {role.features?.length}
                                            </span>
                                        </td>
                                        <td className="nk-tb-col">
                                            <span className="tb-amount" id={`${props.id}-user-count-${key}`}>
                                                {role.user_count}
                                            </span>
                                        </td>
                                        <td className="nk-tb-col">
                                            <span className="text-dark" id={`${props.id}-created-at-date-${key}`}>
                                                {DateFormatter(role?.created_at)}
                                            </span>
                                            <div>
                                                <div className="meta mt-1">
                                                    <span className="version">
                                                        <span className="text-soft" id={`${props.id}-created-at-time-${key}`}>
                                                            {TimeFormatter(role?.created_at)}
                                                        </span>
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {isLoadingMore ? <RoleTableSkeletonDesktop rowCount={2} /> : null}
                            </>
                        ) : (
                            <tr>
                                <td colSpan={4}>
                                    <NoDataAvailable>
                                        <h4 className="text-center" id={`${props.id}-no-data`}>
                                            {NO_ROLE_YET}
                                        </h4>
                                        <p className="text-center" id={`${props.id}-no-data-message`}>
                                            {CREATE_YOUR_ROLES}
                                        </p>
                                    </NoDataAvailable>
                                </td>
                            </tr>
                        )
                    ) : (
                        <RoleTableSkeletonDesktop />
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default RoleTable;
